import axios from 'axios';
// Nu importa nimic din api.js — altfel se creeaza o dependenta circulara.
import { saveReturnTo } from './postLoginDestination';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({ baseURL: API_URL });

// Injectează token-ul din localStorage la fiecare request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('brandvis_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// 2026-05-14: Auto-refresh token pe 401 inainte de redirect la /login.
// Inainte: scanul lung (5 min) expira token-ul (Supabase default 1h) → redirect → user pierde scanul.
// Acum: la primul 401 încercăm POST /auth/refresh; dacă reușește, retry request original.
let _refreshPromise = null;  // single-flight: evita 10x refresh-uri paralele

const _attemptRefresh = async () => {
  if (_refreshPromise) return _refreshPromise;
  const refreshToken = localStorage.getItem('brandvis_refresh_token');
  if (!refreshToken) return null;
  _refreshPromise = (async () => {
    try {
      // Apel direct cu axios crud — NU api (ca să evităm interceptor recursiv).
      const r = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token: refreshToken });
      const newToken = r.data?.access_token;
      const newRefresh = r.data?.refresh_token;
      if (newToken) {
        localStorage.setItem('brandvis_token', newToken);
        if (newRefresh) localStorage.setItem('brandvis_refresh_token', newRefresh);
        return newToken;
      }
    } catch (e) {
      // Refresh eșuat (expired refresh_token, sau endpoint nu există) — clear și redirect.
    }
    return null;
  })();
  try { return await _refreshPromise; } finally { _refreshPromise = null; }
};

// Redirecționează la login dacă 401 sau 403 (HTTPBearer missing/invalid).
// 2026-05-17 — Adăugat tratare 403 "Not authenticated" (FastAPI HTTPBearer
// returnează 403, nu 401, când header-ul Authorization lipsește). Anterior
// 403 nu trigger-a refresh → user vedea "Not authenticated" la scan fără redirect.
api.interceptors.response.use(
  res => res,
  async err => {
    const status = err.response?.status;
    const detail = err.response?.data?.detail;
    const isAuthIssue = status === 401 || (status === 403 && (
      detail === 'Not authenticated' ||
      (typeof detail === 'string' && detail.toLowerCase().includes('not authenticated'))
    ));
    if (isAuthIssue) {
      const url = err.config?.url || '';
      const isAuthAttempt = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/refresh');
      const alreadyOnLoginPage = typeof window !== 'undefined' && window.location.pathname === '/login';
      const alreadyRetried = err.config?._auth_retried === true;
      if (!isAuthAttempt && !alreadyOnLoginPage && !alreadyRetried) {
        // Încercăm refresh înainte de a renunța.
        const newToken = await _attemptRefresh();
        if (newToken) {
          err.config._auth_retried = true;
          err.config.headers.Authorization = `Bearer ${newToken}`;
          return api.request(err.config);
        }
        // Refresh eșuat — clear și redirect (salvăm return URL pentru post-login redirect).
        // 2026-08-19: intenția se salvează cu timestamp (TTL 30 min) prin
        // saveReturnTo. Înainte cheia nu expira și nu se ștergea la logout, deci
        // un 401 abandonat acum trei zile teleporta login-ul de azi, iar pe un
        // browser partajat userul B ajungea pe ultimul URL al lui A.
        try {
          const here = window.location.pathname + window.location.search + window.location.hash;
          saveReturnTo(here);
        } catch {}
        localStorage.removeItem('brandvis_token');
        localStorage.removeItem('brandvis_refresh_token');
        localStorage.removeItem('brandvis_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

// 2026-05-17 — Proactive token refresh: la fiecare 50 min refresh-uim tokenul
// pentru a evita expirarea în mijlocul unui scan lung. Combinat cu refresh token
// (TTL 30 zile la Supabase), user-ul rămâne logat efectiv 3+ ore (sau cât e activ).
// Pornește doar dacă există token salvat (după login).
if (typeof window !== 'undefined') {
  const PROACTIVE_REFRESH_MS = 50 * 60 * 1000;  // 50 min
  setInterval(async () => {
    if (!localStorage.getItem('brandvis_token')) return;
    // Doar dacă tab-ul e vizibil — evităm refresh-uri inutile când user-ul e plecat
    if (document.visibilityState !== 'visible') return;
    try {
      const refreshed = await _attemptRefresh();
      if (refreshed && process.env.NODE_ENV !== 'production') {
        console.debug('[api] proactive token refresh OK');
      }
    } catch {}
  }, PROACTIVE_REFRESH_MS);
}

export const authApi = {
  login: (email, password) => api.post('/api/auth/login', { email, password }),
  register: (data) => api.post('/api/auth/register', data),
  logout: () => api.post('/api/auth/logout'),
  profile: () => api.get('/api/auth/profile'),
  onboarding: (role, industries) => api.post('/api/auth/onboarding', { role, industries }),
  updateIndustries: (industries) => api.put('/api/auth/industries', { industries }),
  usage: () => api.get('/api/auth/usage'),
  gamification: () => api.get('/api/auth/gamification'),
  /** Retine brandul de aterizare post-login. `makePrimary=false` (implicit)
   *  scrie last_brand_id; `true` scrie alegerea explicita a userului. */
  setLandingBrand: (brandId, makePrimary = false) =>
    api.patch('/api/auth/landing-brand', { brand_id: brandId, make_primary: makePrimary }),
  forgotPassword: (email) => api.post('/api/auth/forgot-password', { email }),
  resetPassword: (newPassword) => api.post('/api/auth/reset-password', { new_password: newPassword }),
  updateProfile: (data) => api.put('/api/auth/profile', data),
  changeEmail: (newEmail) => api.put('/api/auth/email', { new_email: newEmail }),
  changePassword: (newPassword) => api.put('/api/auth/password', { new_password: newPassword }),
  deleteAccount: () => api.delete('/api/auth/account'),
};

export const brandsApi = {
  list: () => api.get('/api/brands/'),
  create: (data) => api.post('/api/brands/', data),
  get: (id) => api.get(`/api/brands/${id}`),
  update: (id, data) => api.patch(`/api/brands/${id}`, data),
  delete: (id) => api.delete(`/api/brands/${id}`),
};

export const scoresApi = {
  getQuestions: (industry) => api.get(`/api/scores/questions/${industry}`),
  getQuestionsByCategory: (industry) => api.get(`/api/scores/questions-by-category/${industry}`),
  addQuestion: (data) => api.post('/api/scores/questions', data),
  toggleQuestion: (id) => api.put(`/api/scores/questions/${id}/toggle`),
  deleteQuestion: (id) => api.delete(`/api/scores/questions/${id}`),
  // 10 min timeout — fără asta, dacă proxy-ul drop-uie conexiunea fără
  // să trimită status HTTP, request-ul atârnă indefinit (am văzut 31016s).
  // ECONNABORTED la 10 min duce userul pe ramura "timeout depășit" din BrandDetail.
  calculate: (data) => api.post('/api/scores/calculate', data, { timeout: 600000 }),
  getMarketHistory: (brandId) => api.get(`/api/scores/market-history/${brandId}`),
  getMarketCatalog: (brandId) => api.get(`/api/scores/market-catalog/${brandId}`),
  getBenchmark: (industry, brandName) => api.get(`/api/scores/benchmark/${industry}/${brandName}`),
  history: (brandId, limit = 90) => api.get(`/api/scores/history/${brandId}?limit=${limit}`),
  latest: (brandId) => api.get(`/api/scores/latest/${brandId}`),
  forecast: (brandId) => api.get(`/api/scores/forecast/${brandId}`),
  judgePrompt: (brandId) => api.get(`/api/scores/judge-prompt/${brandId}`),
  // 2026-05-29 — Per-criteriu breakdown (pipeline 4-semnale).
  // Returnează status 'ok' cu per_llm{final_score, criteria[]} sau 'pending' dacă brand-ul
  // n-a fost scanat încă cu noul pipeline (înainte de migration).
  criteriaBreakdown: (brandId) => api.get(`/api/scores/criteria-breakdown/${brandId}`),
  // 2026-05-30 — Reputational Risk Index (composite 7-axe + market share).
  // Returnează status 'ok' cu rri/health_score/axes_data/tier sau 'pending' dacă
  // brand-ul n-a fost scanat încă cu pipeline-ul RRI.
  rri: (brandId) => api.get(`/api/scores/rri/${brandId}`),
  rriHistory: (brandId, limit = 30) => api.get(`/api/scores/rri/${brandId}/history?limit=${limit}`),
  // 2026-07-27 — Audit tehnic (cele 5 check-uri pt boți AI: HTTPS/robots/sitemap/llms.txt/schema.org).
  // Rulează LIVE (audit HTTP real, cache 12h). refresh=true ocolește cache-ul după un fix.
  technicalAudit: (brandId, refresh = false) =>
    api.get(`/api/scores/technical-audit/${brandId}${refresh ? '?refresh=true' : ''}`),
  // Board competitiv per specialitate (cine te bate + rang) — extras din răspunsurile AI.
  competitorBoard: (brandId) => api.get(`/api/scores/competitor-board/${brandId}`),
  // Benchmark RRI vs celelalte branduri RRI ale userului din aceeași industrie.
  medicalBenchmark: (brandId) => api.get(`/api/scores/medical-benchmark/${brandId}`),
  // Citation landscape (à la Profound) — ce domenii citează AI + cota ta.
  citationLandscape: (brandId) => api.get(`/api/scores/citation-landscape/${brandId}`),
  // Source X-Ray (Varianta 1) — atribuire de surse PER ÎNTREBARE + acțiuni.
  sourceXray: (brandId) => api.get(`/api/source-xray/${brandId}`),
  // 2026-05-30 — scan dedicat per axă (1-7). Pentru Axa 1: trigger full scan.
  // Axe 2-5: recalculare din date existente. Axe 6-7: scan cu întrebări dedicate
  // (TODO implementare completă).
  scanAxis: (brandId, axisNumber) => api.post(`/api/scores/rri/${brandId}/scan-axis/${axisNumber}`, null, { timeout: 600000 }),
  // Radar Eveniment AI — metrici REALE per eveniment din ultimul scan (+ delta vs anterior).
  // Status 'ok' cu events[{metrics, responses, delta, event}] sau 'pending' dacă nescanat.
  aiEvents: (brandId) => api.get(`/api/scores/ai-events/${brandId}`),
  // Rulează DOAR radarul (5 întrebări × 4 LLM + judecata Faza 2), fără scan complet.
  runAiEvents: (brandId) => api.post(`/api/scores/ai-events/${brandId}/scan`, null, { timeout: 600000 }),
};

// Per-brand custom products + custom questions (vezi backend/routers/brand_products.py).
// 5 intrebari standard sunt seed-uite la create; user le poate edita/sterge si poate adauga altele.
// Scor: _score_custom_products_local din brand_scoring_service.py — pentru fiecare intrebare,
// se intreaba Gemini + OpenAI si se masoara rata de mentionare a brandului.
export const brandProductsApi = {
  list: (brandId) => api.get(`/api/brand-products/${brandId}`),
  create: (brandId, data) => api.post(`/api/brand-products/${brandId}`, data),
  update: (brandId, productId, data) => api.patch(`/api/brand-products/${brandId}/${productId}`, data),
  delete: (brandId, productId) => api.delete(`/api/brand-products/${brandId}/${productId}`),
};

export const bankingIndicatorsApi = {
  // #11: 10 min timeout — match scoresApi.calculate. Cu indicators rulând în paralel
  // (bound 4 concurrent), full scan tipic ~60-90s; cap 10 min pentru safety.
  scan: (brandId, indicatorKey = null) => {
    const url = indicatorKey
      ? `/api/banking-indicators/scan/${brandId}?indicator_key=${encodeURIComponent(indicatorKey)}`
      : `/api/banking-indicators/scan/${brandId}`;
    return api.post(url, null, { timeout: 600000 });
  },
  get: (brandId, indicatorKey, opts = {}) => api.get(`/api/banking-indicators/${brandId}/${indicatorKey}`, opts),
  freshness: (brandId) => api.get(`/api/banking-indicators/${brandId}`),
  allScores: (brandId, opts = {}) => api.get(`/api/banking-indicators/all-scores/${brandId}`, opts),
};

export const diagnosticIndicatorsApi = {
  // Citation Rate / AI Safety / Impact Score / Brand Vault — generic, non-banking.
  // Timeout mare la fel ca bankingIndicatorsApi (indicators rulează în paralel,
  // dar impact_score poate declanșa un crawl Brand Vault sincron la primul scan).
  scan: (brandId, indicatorKey = null) => {
    const url = indicatorKey
      ? `/api/diagnostic-indicators/scan/${brandId}?indicator_key=${encodeURIComponent(indicatorKey)}`
      : `/api/diagnostic-indicators/scan/${brandId}`;
    return api.post(url, null, { timeout: 600000 });
  },
  get: (brandId, indicatorKey, opts = {}) => api.get(`/api/diagnostic-indicators/${brandId}/${indicatorKey}`, opts),
  freshness: (brandId) => api.get(`/api/diagnostic-indicators/${brandId}`),
  allScores: (brandId, opts = {}) => api.get(`/api/diagnostic-indicators/all-scores/${brandId}`, opts),
};

export const aiOverviewApi = {
  // Google AI Overview — modul distinct de semnal (SerpAPI, gl=ro).
  // Max 12 cautari Google per scan, concurenta 4 → worst case ~2-3 min
  // cu retry-uri; 5 min timeout acopera confortabil.
  status: () => api.get('/api/ai-overview/status'),
  scan: (brandId) => api.post(`/api/ai-overview/scan/${brandId}`, null, { timeout: 300000 }),
  latest: (brandId) => api.get(`/api/ai-overview/latest/${brandId}`),
  history: (brandId, limit = 30) => api.get(`/api/ai-overview/history/${brandId}?limit=${limit}`),
};

export const exportApi = {
  listKeys: () => api.get('/api/export/keys'),
  createKey: (name) => api.post(`/api/export/keys?name=${encodeURIComponent(name || 'default')}`),
  revokeKey: (keyId) => api.delete(`/api/export/keys/${keyId}`),
};

// Acquisition hooks — public, no auth
// Timeout explicit: fara el, axios asteapta la infinit, iar butonul ramane in
// "⏳ ANALIZEZ" pentru totdeauna daca reteaua cade.
// mini-scan v2: worst case server = clasificare (~63s: 2-3 incercari x 20s
// timeout LLM + backoff) + cele 9 conversatii in PARALEL (~63s) + judge (60s,
// o incercare) ≈ 190s. Tipic 15-30s; 190s acopera doar cazul legitim extrem.
// `industry`/`sector_hint` raman in semnatura pentru compatibilitate — backendul
// le accepta si le ignora (clasificarea e dinamica din 2026-08-14).
export const acquisitionApi = {
  // Pasul 0: clasificarea + cele 9 intrebari, INAINTE de scan, ca userul sa le
  // poata citi cat ruleaza. Costa un singur apel LLM — acelasi pe care il facea
  // scanul, mutat mai devreme; `prep_token` il duce mai departe semnat, deci
  // intrebarile afisate sunt exact cele puse. Timeout 45s: e un apel de
  // clasificare (~2-5s tipic) peste un posibil cold start Cloud Run.
  // Pasul 1 al mini-scanului: din ce industrie face parte brandul. Raspunde
  // instant pentru brandurile pe care le stim deja (registru / scanare
  // anterioara / `brands`), cu un singur apel LLM mic pentru restul — de aceea
  // timeout-ul e mult sub cel al pasului 2.
  miniScanClassify: (brand_name) =>
    api.post('/api/public/mini-scan/classify', { brand_name }, { timeout: 20000 }),
  // Pasul 2: concurentii, produsele si cele 9 intrebari. `options.stage_token`
  // vine de la miniScanClassify; lipsa lui nu e o eroare (se re-identifica).
  miniScanPrepare: (brand_name, options = {}) =>
    api.post(
      '/api/public/mini-scan/prepare',
      { brand_name, stage_token: options.stage_token || null },
      { timeout: 45000 },
    ),
  // `options.prep_token` vine de la miniScanPrepare. Lipsa lui nu e o eroare:
  // backendul reclasifica, exact ca inainte.
  miniScan: (brand_name, options = {}) =>
    api.post('/api/public/mini-scan', { brand_name, prep_token: options.prep_token || null }, { timeout: 190000 }),
  detectIndustry: (brand_name) =>
    api.get('/api/public/detect-industry', { params: { brand_name }, timeout: 8000 }),
  miniScanLead: (payload) =>
    api.post('/api/public/mini-scan/lead', payload, { timeout: 15000 }),
  // Rezultatul salvat al unui mini-scan — sursa paginii publice /scan/:scan_id.
  // Payload imuabil (un scan nu se rescrie), deci raspunsul e cache-uit 1h la CDN.
  // 404 = scan inexistent / dinainte de migratia `payload` / brand nepublicabil.
  // Timeout 30s si nu 8-15s: backendul ruleaza cu min-instances=0, iar primul
  // click pe un link distribuit poate prinde un cold start.
  miniScanResult: (scan_id) =>
    api.get(`/api/public/mini-scan/${encodeURIComponent(scan_id)}`, { timeout: 30000 }),
  brandBattle: (brand_a, brand_b, industry) =>
    api.post('/api/public/brand-battle', { brand_a, brand_b, industry }, { timeout: 90000 }),
};

export const adminApi = {
  getRules: () => api.get('/api/admin/rules'),
  createRule: (data) => api.post('/api/admin/rules', data),
  activateRule: (id) => api.put(`/api/admin/rules/${id}/activate`),
  deleteRule: (id) => api.delete(`/api/admin/rules/${id}`),
  getUsers: () => api.get('/api/admin/users'),
  updateUserRole: (userId, role) => api.put(`/api/admin/users/${userId}/role?role=${role}`),
  getStats: () => api.get('/api/admin/stats'),
  getTopPlayers: () => api.get('/api/admin/top-players'),
  runIndustrySetup: (industry) => api.post('/api/admin/industry-setup', { industry }, { timeout: 600000 }),
  grantUnlimitedAccess: (data) => api.post('/api/admin/unlimited-emails/grant', data),
  // Conturi create care asteapta aprobare (nu sunt in unlimited_emails).
  getPendingUsers: () => api.get('/api/admin/pending-users'),
};

// Monitor modul alerts (read-only, doar admin) — rulari batch LLM, alerte, stiri.
export const alertsMonitorApi = {
  getRuns: (limit = 20) => api.get(`/api/alerts-monitor/runs?limit=${limit}`),
  getRun: (runId) => api.get(`/api/alerts-monitor/runs/${runId}`),
  getAlerts: (limit = 50) => api.get(`/api/alerts-monitor/alerts?limit=${limit}`),
  sendFeedback: (alertId, verdict) => api.post(`/api/alerts-monitor/alerts/${alertId}/feedback`, { verdict }),
  getFeedbackStats: () => api.get('/api/alerts-monitor/feedback-stats'),
  getNews: (limit = 50) => api.get(`/api/alerts-monitor/news?limit=${limit}`),
};

// Jurnalul modulului alerts PER BRAND (proprietarul brandului, nu doar admin):
// ce s-a trimis catre motoare, cand, cum, ce s-a primit, ce linkuri au iesit.
// Tot ce intoarce e paginat pe server — un brand face 400-800 de apeluri/zi.
export const alertsBrandApi = {
  getOverview: (brandId, params = {}) => api.get(`/api/alerts-brand/${brandId}/overview`, { params }),
  getCalls: (brandId, params = {}) => api.get(`/api/alerts-brand/${brandId}/calls`, { params }),
  getCall: (brandId, callId, includeRaw = false) =>
    api.get(`/api/alerts-brand/${brandId}/calls/${callId}`, { params: { include_raw: includeRaw } }),
  getPrompts: (brandId, params = {}) => api.get(`/api/alerts-brand/${brandId}/prompts`, { params }),
  getLinks: (brandId, params = {}) => api.get(`/api/alerts-brand/${brandId}/links`, { params }),
  getAlerts: (brandId, params = {}) => api.get(`/api/alerts-brand/${brandId}/alerts`, { params }),
  // Loveste prin HTTP linkurile fara verdict (sau cu verdict vechi) si scrie
  // rezultatul in registru. Timeout generos: verificarea are I/O de retea, cu
  // buget propriu pe server (25s per lot).
  verifyLinks: (brandId, params = {}) =>
    api.post(`/api/alerts-brand/${brandId}/links/verify`, null, { params, timeout: 120000 }),
  // Prezența în presă: știrile despre brand + comutatorul de monitorizare.
  // `setMediaMonitoring` e singura scriere din routerul alerts-brand — la prima
  // pornire provisionează brandul (termenii după care e căutat în știri).
  getNews: (brandId, params = {}) => api.get(`/api/alerts-brand/${brandId}/news`, { params }),
  setMediaMonitoring: (brandId, enabled) =>
    api.post(`/api/alerts-brand/${brandId}/media-monitoring`, { enabled }),
};

export const llmApi = {
  previewQuestions: (data) => api.post('/api/llm/preview-questions', data),
  getIndustries: () => api.get('/api/llm/industries'),
  // Playground admin — apel brut catre LLM-uri. Timeout generos: modelele mari
  // pot depasi minutul, iar backend-ul are PLAYGROUND_TIMEOUT=120s per furnizor.
  playgroundConfig: () => api.get('/api/llm/playground/config'),
  playgroundRun: (data) => api.post('/api/llm/playground', data, { timeout: 180000 }),
  // Prefill din modulul de alerte — ultimul prompt trimis efectiv pentru un brand.
  playgroundBrands: () => api.get('/api/llm/playground/brands'),
  playgroundLastPrompt: (brand) => api.get('/api/llm/playground/last-prompt', { params: { brand } }),
};

export const billingApi = {
  // plan: 'start' | 'professional'. 'enterprise' NU trece prin checkout —
  // se vinde pe oferta si se activeaza manual din admin.
  checkoutSession: (plan = 'professional') => api.post('/api/billing/checkout-session', { plan }),
  portal: () => api.post('/api/billing/portal'),
};

// Bump aceasta valoare cand modificam lista de industrii ca sa invalidam
// cache-ul serverului si al browserului (e folosita ca query param `?v=`).
const INDUSTRIES_VERSION = '20260510';

export const publicApi = {
  industries: () => api.get(`/api/public/industries?v=${INDUSTRIES_VERSION}`),
  stats: () => api.get('/api/public/stats'),
  brands: (industry) => api.get('/api/public/brands', { params: industry ? { industry } : {} }),
  brand: (industry, slug) => api.get(`/api/public/brand/${industry}/${slug}`),
  demoRequest: (data) => api.post('/api/public/demo-request', data),
  leaderboard: (industry, limit = 20) => api.get(`/api/public/leaderboard/${industry}`, { params: { limit } }),
  rriLeaderboard: (industry, limit = 50) => api.get(`/api/public/rri-leaderboard/${industry}`, { params: { limit } }),
  activityFeed: (limit = 10) => api.get('/api/public/activity-feed', { params: { limit } }),
  industryCriteria: (industry) => api.get(`/api/public/industry-criteria/${industry}`, { params: { t: Date.now() } }),
  industryBrandScores: (industry, limit = 50) => api.get(`/api/public/industry-brand-scores/${industry}`, { params: { limit } }),
};

export default api;
